import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GlassCard, GlassSection } from './GlassSurface';
import { GlassCtaButton } from './GlassCtaButton';
import { GlassChip } from './GlassChip';
import { GlassInput } from './GlassInput';
import { GlassSpinner } from './GlassSpinner';
import { GlassTooltip } from './GlassTooltip';
import {
  fetchSpaceCalendar,
  blockCalendarSlot,
  deleteCalendarEntry
} from '../../Apiservices/Auth/servers/venueServices';
import './VenueSpaceCalendar.css';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Date Status Helper
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const getDateStatus = (date, entries) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  const matchedEntry = entries.find(entry => {
    const fromDate = entry.fromDate.split('T')[0];
    const toDate = entry.toDate.split('T')[0];
    return dateStr >= fromDate && dateStr <= toDate;
  });

  if (matchedEntry) {
    if (matchedEntry.type === 'booking') {
      return { status: 'booking', entry: matchedEntry };
    } else if (matchedEntry.type === 'blocked') {
      return { status: 'blocked', entry: matchedEntry };
    }
  }
  return { status: 'available', entry: null };
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main Calendar Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const VenueSpaceCalendar = ({ 
  venueId, 
  spaceId, 
  spaceName,
  onClose,
  onBlockSuccess,
  onUnblockSuccess
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Block dialog state
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [fromTime, setFromTime] = useState(null);
  const [toTime, setToTime] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const scrollRef = useRef(null);

  // ─── Colors ───
  const COLORS = {
    free: '#38B6FF',
    booked: '#FF895D',
    blocked: '#000000',
  };

  // ─── Fetch Calendar ───
  const fetchCalendar = useCallback(async () => {
    setIsLoading(true);
    try {
      const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
      const data = await fetchSpaceCalendar(venueId, spaceId, monthKey);
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    } finally {
      setIsLoading(false);
    }
  }, [venueId, spaceId, currentMonth]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  // ─── Navigation ───
  const prevMonth = useCallback(() => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  }, [currentMonth]);

  const nextMonth = useCallback(() => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  }, [currentMonth]);

  // ─── Date Helpers ───
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  };

  const isPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // ─── Cell Click Handler ───
  const handleDateClick = useCallback((date) => {
    if (isPast(date)) return;
    
    const dateStatus = getDateStatus(date, entries);
    setSelectedDate(date);
    
    if (dateStatus.status === 'available') {
      setFromDate(date);
      setToDate(date);
      setFromTime(null);
      setToTime(null);
      setBlockReason('');
      setShowBlockDialog(true);
    } else if (dateStatus.status === 'blocked' && !dateStatus.entry?.bookingId) {
      setSelectedEntry(dateStatus.entry);
      setShowUnblockDialog(true);
    } else if (dateStatus.status === 'booking') {
      // Show booking info
      alert(`This date is booked.\nBooking ID: ${dateStatus.entry?.bookingId || 'N/A'}`);
    }
  }, [entries]);

  // ─── Block Handler ───
  const handleBlock = useCallback(async () => {
    if (!fromDate || !toDate) {
      alert('Please select both from and to dates');
      return;
    }

    if (toDate < fromDate) {
      alert('End date cannot be before start date');
      return;
    }

    if (!blockReason.trim()) {
      alert('Please provide a reason for blocking');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        venueId,
        spaceId,
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
        type: 'blocked',
        reason: blockReason.trim(),
        ...(fromTime && { fromTime: fromTime }),
        ...(toTime && { toTime: toTime }),
      };

      const success = await blockCalendarSlot(payload);
      if (success) {
        await fetchCalendar();
        setShowBlockDialog(false);
        setBlockReason('');
        if (onBlockSuccess) onBlockSuccess();
      } else {
        alert('Failed to block dates');
      }
    } catch (error) {
      console.error('Error blocking dates:', error);
      alert('Error blocking dates: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [venueId, spaceId, fromDate, toDate, fromTime, toTime, blockReason, fetchCalendar, onBlockSuccess]);

  // ─── Unblock Handler ───
  const handleUnblock = useCallback(async () => {
    if (!selectedEntry?.id) return;

    setIsSubmitting(true);
    try {
      const success = await deleteCalendarEntry(selectedEntry.id);
      if (success) {
        await fetchCalendar();
        setShowUnblockDialog(false);
        setSelectedEntry(null);
        if (onUnblockSuccess) onUnblockSuccess();
      } else {
        alert('Failed to unblock date');
      }
    } catch (error) {
      console.error('Error unblocking date:', error);
      alert('Error unblocking date: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedEntry, fetchCalendar, onUnblockSuccess]);

  // ─── Render Calendar Grid ───
  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const today = new Date();
    const dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    
    const cells = [];
    
    // Day labels
    dayLabels.forEach((label, index) => {
      cells.push(
        <div key={`label-${index}`} className="calendar-cell-label">
          {label}
        </div>
      );
    });

    // Empty cells for days before first day
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="calendar-cell-empty" />);
    }

    // Date cells
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dateStatus = getDateStatus(date, entries);
      const isTodayDate = isToday(date);
      const isPastDate = isPast(date);
      
      let statusColor = COLORS.free;
      let bgColor = 'transparent';
      let textColor = isPastDate ? '#ccc' : '#333';
      let dotColor = isPastDate ? '#ddd' : COLORS.free;
      
      if (dateStatus.status === 'booking') {
        statusColor = COLORS.booked;
        textColor = COLORS.booked;
        dotColor = COLORS.booked;
        bgColor = 'rgba(255, 137, 93, 0.08)';
      } else if (dateStatus.status === 'blocked') {
        statusColor = COLORS.blocked;
        textColor = '#ffffff';
        dotColor = COLORS.blocked;
        bgColor = COLORS.blocked;
      }

      cells.push(
        <div
          key={`day-${day}`}
          className={`calendar-cell ${isPastDate ? 'past' : ''} ${dateStatus.status}`}
          style={{
            backgroundColor: bgColor,
            border: isTodayDate ? `2px solid ${COLORS.free}` : 'none',
            cursor: isPastDate ? 'not-allowed' : 'pointer',
          }}
          onClick={() => handleDateClick(date)}
        >
          <div className="calendar-cell-content">
            <span 
              className="calendar-cell-date"
              style={{ color: textColor }}
            >
              {day}
            </span>
            <div 
              className="calendar-cell-dot"
              style={{ backgroundColor: dotColor }}
            />
          </div>
        </div>
      );
    }

    return cells;
  };

  // ─── Render Legend ───
  const renderLegend = () => (
    <div className="calendar-legend">
      <div className="legend-item">
        <div className="legend-dot" style={{ backgroundColor: COLORS.free }} />
        <span className="legend-label">Free</span>
      </div>
      <div className="legend-item">
        <div className="legend-dot" style={{ backgroundColor: COLORS.blocked }} />
        <span className="legend-label">Blocked</span>
      </div>
      <div className="legend-item">
        <div className="legend-dot" style={{ backgroundColor: COLORS.booked }} />
        <span className="legend-label">Booked</span>
      </div>
    </div>
  );

  // ─── Render Block Dialog ───
  const renderBlockDialog = () => {
    if (!showBlockDialog) return null;

    return (
      <div className="calendar-dialog-overlay" onClick={() => setShowBlockDialog(false)}>
        <div className="calendar-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="dialog-header">
            <h3>Block Date(s)</h3>
            <button className="dialog-close" onClick={() => setShowBlockDialog(false)}>×</button>
          </div>
          <div className="dialog-body">
            <p className="dialog-subtitle">Prevent bookings on these dates</p>

            <div className="dialog-date-row">
              <div className="dialog-date-group">
                <label>From Date *</label>
                <input
                  type="date"
                  value={formatDate(fromDate)}
                  min={formatDate(new Date())}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value.replace(/-/g, '/'));
                    setFromDate(newDate);
                    if (toDate < newDate) setToDate(newDate);
                  }}
                  className="dialog-date-input"
                />
              </div>
              <div className="dialog-date-group">
                <label>To Date *</label>
                <input
                  type="date"
                  value={formatDate(toDate)}
                  min={formatDate(fromDate)}
                  onChange={(e) => setToDate(new Date(e.target.value.replace(/-/g, '/')))}
                  className="dialog-date-input"
                />
              </div>
            </div>

            <div className="dialog-time-row">
              <div className="dialog-time-group">
                <label>From Time (optional)</label>
                <input
                  type="time"
                  value={fromTime || ''}
                  onChange={(e) => setFromTime(e.target.value)}
                  className="dialog-time-input"
                />
                {fromTime && (
                  <button className="dialog-time-clear" onClick={() => setFromTime(null)}>
                    ×
                  </button>
                )}
              </div>
              <div className="dialog-time-group">
                <label>To Time (optional)</label>
                <input
                  type="time"
                  value={toTime || ''}
                  onChange={(e) => setToTime(e.target.value)}
                  className="dialog-time-input"
                />
                {toTime && (
                  <button className="dialog-time-clear" onClick={() => setToTime(null)}>
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="dialog-reason">
              <label>Reason *</label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="e.g. Maintenance, Private event, etc."
                className="dialog-reason-input"
                rows={3}
              />
            </div>

            <div className="dialog-actions">
              <button 
                className="dialog-btn cancel"
                onClick={() => setShowBlockDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="dialog-btn block"
                onClick={handleBlock}
                disabled={isSubmitting || !blockReason.trim()}
              >
                {isSubmitting ? 'Blocking...' : 'Block Dates'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render Unblock Dialog ───
  const renderUnblockDialog = () => {
    if (!showUnblockDialog || !selectedEntry) return null;

    const fromDateObj = new Date(selectedEntry.fromDate.replace(/-/g, '/'));
    const toDateObj = new Date(selectedEntry.toDate.replace(/-/g, '/'));

    return (
      <div className="calendar-dialog-overlay" onClick={() => setShowUnblockDialog(false)}>
        <div className="calendar-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="dialog-header">
            <h3>Unblock Date(s)</h3>
            <button className="dialog-close" onClick={() => setShowUnblockDialog(false)}>×</button>
          </div>
          <div className="dialog-body">
            <p className="dialog-subtitle">
              Unblock {formatDisplayDate(fromDateObj)}
              {selectedEntry.fromDate !== selectedEntry.toDate && 
                ` - ${formatDisplayDate(toDateObj)}`}
              ?
            </p>
            
            {selectedEntry.reason && (
              <div className="dialog-info">
                <span className="dialog-info-label">Reason:</span>
                <span>{selectedEntry.reason}</span>
              </div>
            )}
            
            {(selectedEntry.fromTime || selectedEntry.toTime) && (
              <div className="dialog-info">
                <span className="dialog-info-label">Time:</span>
                <span>
                  {selectedEntry.fromTime}
                  {selectedEntry.toTime && ` - ${selectedEntry.toTime}`}
                </span>
              </div>
            )}

            <div className="dialog-actions">
              <button 
                className="dialog-btn cancel"
                onClick={() => setShowUnblockDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="dialog-btn unblock"
                onClick={handleUnblock}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Unblocking...' : 'Unblock'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render Blocked Dates List ───
  const renderBlockedDatesList = () => {
    const blockedEntries = entries.filter(e => e.type === 'blocked');
    if (blockedEntries.length === 0) return null;

    return (
      <div className="blocked-dates-list">
        <div className="blocked-dates-header">
          <span className="blocked-dates-title">Blocked Dates</span>
          <GlassChip label={`${blockedEntries.length} blocked`} variant="error" />
        </div>
        {blockedEntries.map((entry, index) => {
          const fromDateObj = new Date(entry.fromDate.replace(/-/g, '/'));
          const toDateObj = new Date(entry.toDate.replace(/-/g, '/'));
          const isSingleDay = entry.fromDate === entry.toDate;
          
          return (
            <div key={entry.id || index} className="blocked-date-item">
              <div className="blocked-date-info">
                <span className="blocked-date-range">
                  {formatDisplayDate(fromDateObj)}
                  {!isSingleDay && ` - ${formatDisplayDate(toDateObj)}`}
                </span>
                {entry.reason && (
                  <span className="blocked-date-reason">{entry.reason}</span>
                )}
                {(entry.fromTime || entry.toTime) && (
                  <span className="blocked-date-time">
                    {entry.fromTime}{entry.toTime && ` - ${entry.toTime}`}
                  </span>
                )}
              </div>
              <button 
                className="blocked-date-unblock"
                onClick={() => {
                  setSelectedEntry(entry);
                  setShowUnblockDialog(true);
                }}
              >
                Unblock
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Main Render ───
  return (
    <div className="venue-space-calendar">
      {/* Header */}
      <div className="calendar-header">
        <div className="calendar-title">
          <span className="calendar-icon">📅</span>
          <span className="calendar-name">{spaceName}</span>
        </div>
        <button 
          className="calendar-expand-btn"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? '▼' : '▲'}
        </button>
      </div>

      {isExpanded && (
        <div className="calendar-content">
          {/* Month Navigation */}
          <div className="calendar-nav">
            <button className="calendar-nav-btn" onClick={prevMonth}>
              ◀
            </button>
            <span className="calendar-month">
              {currentMonth.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            <button className="calendar-nav-btn" onClick={nextMonth}>
              ▶
            </button>
          </div>

          {/* Legend */}
          {renderLegend()}

          {/* Calendar Grid */}
          {isLoading ? (
            <div className="calendar-loading">
              <GlassSpinner size={30} />
              <span>Loading calendar...</span>
            </div>
          ) : (
            <div className="calendar-grid" ref={scrollRef}>
              {renderCalendarGrid()}
            </div>
          )}

          {/* Blocked Dates List */}
          {!isLoading && renderBlockedDatesList()}
        </div>
      )}

      {/* Dialogs */}
      {renderBlockDialog()}
      {renderUnblockDialog()}

      {/* Footer */}
      <div className="calendar-footer">
        <button 
          className="calendar-footer-btn"
          onClick={onClose}
        >
          Close Calendar
        </button>
      </div>
    </div>
  );
};

export default VenueSpaceCalendar;